// src/feed/usePhotoAnnotations.ts
//
// Client subscription for the background photo-annotation pipeline.
//
// The annotatePhotoOnUpload Cloud Function writes to
// /photo_annotations/{sha256(storage_path)}. The client derives
// the same id via Web Crypto and subscribes to the doc so dots
// auto-appear once annotations are ready.
//
// Three states surface to the caller:
//
//   • loading   — no doc yet; trigger may still be in flight or the
//                 image isn't user-uploaded (no annotations coming).
//   • pending   — doc exists, function is still working.
//   • complete  — annotations array ready to render.
//   • failed    — function errored out; client should fall back to
//                 the un-annotated image silently.
//
// The hook accepts EITHER a storage path (e.g.
// "images/{uid}/temp_xxx/yyy") OR a fully-qualified URL — only the
// path form gets annotations, since URLs to remote CDN assets
// aren't user uploads.

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

export interface PhotoAnnotation {
  id: string;
  label: string;
  explanation: string;
  /** Optional richer explanation (3–6 sentences) for features that
   *  merit deeper reading. The renderer surfaces a "More details"
   *  button only when this field is present and non-empty. Older
   *  annotations written before this field existed will simply not
   *  show the button — silent degradation. */
  more_details?: string;
  category: string;
  /** Identifier confidence in [0,1]. Drives voice calibration in
   *  the renderer: low confidence prefixes the label with a
   *  hedge ("Appears to be…") so the UI doesn't claim certainty
   *  the model didn't have. Older annotations without this field
   *  default to 0.5 in consumers. */
  confidence?: number;
  /** Notability in [0,1] — the vision pass's self-assessment. */
  notability?: number;
  /** Final ensemble relevance in [0,1] — average of critic scores
   *  when the critic ensemble ran successfully, otherwise falls
   *  back to notability. The server filter drops sub-floor items
   *  before write, so any annotation reaching the client is
   *  expected to have passed the threshold. Surfaced primarily
   *  for future client-side ranking; the renderer treats every
   *  persisted annotation as worth showing. */
  relevance?: number;
  /** Top-left + size, normalized 0..1. */
  box: { x: number; y: number; w: number; h: number };
  /** Center of box, normalized 0..1. Convenience for dot placement. */
  anchor: { x: number; y: number };
}

export type AnnotationStatus =
  | 'idle'
  | 'loading'
  | 'pending'
  | 'complete'
  | 'failed';

export interface UsePhotoAnnotationsResult {
  status: AnnotationStatus;
  annotations: PhotoAnnotation[];
}

/** True if the input looks like a Firebase Storage object path
 *  (not a URL, not a data URL, not blank). */
const isStoragePath = (input: string): boolean => {
  if (!input) return false;
  if (/^(https?:|data:|blob:)/i.test(input)) return false;
  return input.includes('/');
};

/** SHA-256 hex of a UTF-8 string via Web Crypto. */
async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const idCache = new Map<string, string>();

export function usePhotoAnnotations(
  storagePathOrUrl: string | null | undefined
): UsePhotoAnnotationsResult {
  const [docId, setDocId] = useState<string | null>(() => {
    if (!storagePathOrUrl) return null;
    return idCache.get(storagePathOrUrl) ?? null;
  });
  const [status, setStatus] = useState<AnnotationStatus>('idle');
  const [annotations, setAnnotations] = useState<PhotoAnnotation[]>([]);

  useEffect(() => {
    if (!storagePathOrUrl || !isStoragePath(storagePathOrUrl)) {
      setDocId(null);
      setStatus('idle');
      setAnnotations([]);
      return;
    }
    const cached = idCache.get(storagePathOrUrl);
    if (cached) {
      setDocId(cached);
      return;
    }
    let cancelled = false;
    sha256Hex(storagePathOrUrl).then((id) => {
      if (cancelled) return;
      idCache.set(storagePathOrUrl, id);
      setDocId(id);
    });
    return () => {
      cancelled = true;
    };
  }, [storagePathOrUrl]);

  useEffect(() => {
    if (!docId) {
      setStatus('idle');
      setAnnotations([]);
      return;
    }
    setStatus('loading');
    setAnnotations([]);
    const ref = doc(db, 'photo_annotations', docId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setStatus('loading');
          setAnnotations([]);
          return;
        }
        const data = snap.data();
        const docStatus = data?.status as AnnotationStatus | undefined;
        if (docStatus === 'complete') {
          const list = Array.isArray(data?.annotations)
            ? (data.annotations as PhotoAnnotation[])
            : [];
          setStatus('complete');
          setAnnotations(list);
          return;
        }
        if (docStatus === 'failed') {
          setStatus('failed');
          setAnnotations([]);
          return;
        }
        setStatus('pending');
        setAnnotations([]);
      },
      (err) => {
        console.warn('usePhotoAnnotations: subscribe failed', err);
        setStatus('failed');
        setAnnotations([]);
      }
    );
    return () => unsub();
  }, [docId]);

  return { status, annotations };
}
